import {AfterViewChecked, Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {BaseComponent} from "../../../BaseComponent";

@Component({
  selector: 'app-cron-selector',
  templateUrl: './cron-selector.component.html',
  styleUrls: ['./cron-selector.component.css']
})
export class CronSelectorComponent extends BaseComponent implements OnChanges, AfterViewChecked {

  private el = inject(ElementRef);

  ngAfterViewChecked(): void {
    const containers = this.el.nativeElement.querySelectorAll('.cron-tag-box .dx-tag-container:not([data-wheel-bound])');
    containers.forEach((container: HTMLElement) => {
      container.setAttribute('data-wheel-bound', '1');
      container.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });
    });
  }


  @Input() value: string = '* * * * *';
  @Output() cronChange: EventEmitter<string> = new EventEmitter<string>();

  cron = "* * * * *";
  private lastEmitted = "";

  minuteOptions: string[];
  selectedMinute: string[];

  hourOptions: string[];
  selectedHour: string[];

  daysOptions: string[];
  selectedDay: string[];

  monthsOptions: string[];
  selectedMonth: string[];

  weekdaysOptions: string[];
  selectedWeekday: string[];

  periodOptions: string[];
  selectedPeriod: string;

  constructor() {

    super();

    this.minuteOptions = [];
    for (let i = 0; i < 60; i++) {
      const j = (i < 10) ? "0" : "";
      this.minuteOptions.push("" + j + i);
    }

    this.hourOptions = [];
    for (let i = 0; i < 24; i++) {
      const j = (i < 10) ? "0" : "";
      this.hourOptions.push("" + j + i);
    }

    this.daysOptions = [];
    for (let i = 1; i < 32; i++) {
      let suffix;
      if (i == 1 || i == 21 || i == 31) {
        suffix = this.translate.instant('REGISTRY.VIRTUAL_METRICS.ST');
      } else if (i == 2 || i == 22) {
        suffix = this.translate.instant('REGISTRY.VIRTUAL_METRICS.ND');
      } else if (i == 3 || i == 23) {
        suffix = this.translate.instant('REGISTRY.VIRTUAL_METRICS.RD');
      } else {
        suffix = this.translate.instant('REGISTRY.VIRTUAL_METRICS.TH');
      }
      this.daysOptions.push("" + i + suffix);
    }

    this.monthsOptions = ["CRON.JANUARY", "CRON.FEBRUARY", "CRON.MARCH", "CRON.APRIL",
      "CRON.MAY", "CRON.JUNE", "CRON.JULY", "CRON.AUGUST",
      "CRON.SEPTEMBER", "CRON.OCTOBER", "CRON.NOVEMBER", "CRON.DECEMBER"];


    this.weekdaysOptions = ["CRON.SUNDAY", "CRON.MONDAY", "CRON.TUESDAY", "CRON.WEDNESDAY", "CRON.THURSDAY",
      "CRON.FRIDAY", "CRON.SATURDAY"];


    this.periodOptions = ["CRON.MINUTE", "CRON.HOUR", "CRON.DAY", "CRON.WEEK", "CRON.MONTH", "CRON.YEAR"];


    this.selectedMinute = [this.minuteOptions[0]];
    this.selectedHour = [this.hourOptions[0]];
    this.selectedDay = [this.daysOptions[0]];
    this.selectedMonth = [this.monthsOptions[0]];
    this.selectedWeekday = [this.weekdaysOptions[0]];

    this.selectedPeriod = "CRON.MINUTE";
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.value && this.value !== this.lastEmitted) {
      this.parseValue(this.value);
    }
  }

  private parseValue(cron: string): void {
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) {
      this.selectedPeriod = 'CRON.MINUTE';
      return;
    }
    const [min, hour, day, month, dow] = parts;

    if (min === '*') {
      this.selectedPeriod = 'CRON.MINUTE';
    } else if (hour === '*' && day === '*' && month === '*' && dow === '*') {
      this.selectedPeriod = 'CRON.HOUR';
      this.selectedMinute = this.parseToOptions(min, this.minuteOptions, false);
    } else if (day === '*' && month === '*' && dow === '*') {
      this.selectedPeriod = 'CRON.DAY';
      this.selectedMinute = this.parseToOptions(min, this.minuteOptions, false);
      this.selectedHour   = this.parseToOptions(hour, this.hourOptions, false);
    } else if (day === '*' && month === '*') {
      this.selectedPeriod  = 'CRON.WEEK';
      this.selectedMinute  = this.parseToOptions(min, this.minuteOptions, false);
      this.selectedHour    = this.parseToOptions(hour, this.hourOptions, false);
      this.selectedWeekday = this.parseToOptions(dow, this.weekdaysOptions, false);
    } else if (month === '*' && dow === '*') {
      this.selectedPeriod = 'CRON.MONTH';
      this.selectedMinute = this.parseToOptions(min, this.minuteOptions, false);
      this.selectedHour   = this.parseToOptions(hour, this.hourOptions, false);
      this.selectedDay    = this.parseToOptions(day, this.daysOptions, true);
    } else if (dow === '*') {
      this.selectedPeriod  = 'CRON.YEAR';
      this.selectedMinute  = this.parseToOptions(min, this.minuteOptions, false);
      this.selectedHour    = this.parseToOptions(hour, this.hourOptions, false);
      this.selectedDay     = this.parseToOptions(day, this.daysOptions, true);
      this.selectedMonth   = this.parseToOptions(month, this.monthsOptions, true);
    } else {
      this.selectedPeriod = 'CRON.MINUTE';
    }
  }

  private parseToOptions(part: string, options: string[], plusOne: boolean): string[] {
    if (part === '*') return [options[0]];
    return part.split(',').map(s => {
      const idx = Number.parseInt(s, 10) - (plusOne ? 1 : 0);
      return options[idx] ?? options[0];
    }).filter(v => v != null);
  }

  changeEvent() {
    this.cron = this.getCronString();
    this.lastEmitted = this.cron;
    this.cronChange.emit(this.cron);
  }

  getCronString() {

    let min, hour, day, month, dow;
    min = "*";
    hour = "*";
    day = "*";
    month = "*";
    dow = "*";

    switch (this.selectedPeriod) {
      case "CRON.MINUTE":
        break;
      case "CRON.HOUR":
        min = this.getSelectedValue(this.selectedMinute, this.minuteOptions);
        break;
      case "CRON.DAY":
        min = this.getSelectedValue(this.selectedMinute, this.minuteOptions);
        hour = this.getSelectedValue(this.selectedHour, this.hourOptions);
        break;
      case "CRON.WEEK":
        min = this.getSelectedValue(this.selectedMinute, this.minuteOptions);
        hour = this.getSelectedValue(this.selectedHour, this.hourOptions);
        dow = this.getSelectedValue(this.selectedWeekday, this.weekdaysOptions);
        break;
      case "CRON.MONTH":
        min = this.getSelectedValue(this.selectedMinute, this.minuteOptions);
        hour = this.getSelectedValue(this.selectedHour, this.hourOptions);
        day = this.getSelectedValue(this.selectedDay, this.daysOptions, true);
        break;
      case "CRON.YEAR":
        min = this.getSelectedValue(this.selectedMinute, this.minuteOptions);
        hour = this.getSelectedValue(this.selectedHour, this.hourOptions);
        day = this.getSelectedValue(this.selectedDay, this.daysOptions, true);
        month = this.getSelectedValue(this.selectedMonth, this.monthsOptions, true);
        break;
      default:
        return this.cron;
    }
    return [min, hour, day, month, dow].join(" ");
  }

  getSelectedValue(values: string[], options: string[], plusOne?: boolean) {
    let idxsToReturn: number[] = [];

    for (let value of values) {
      let idxToAdd = options.indexOf(value);
      if (plusOne)
        idxToAdd++;
      idxsToReturn.push(idxToAdd);
    }

    if (idxsToReturn.length === 0)
      return "*";

    return idxsToReturn.join(",");
  }

  translateCronExpr = (value: string) => {
    return this.translate.instant("REGISTRY.VIRTUAL_METRICS." + value);
  }
}
